/*
 * Author: Ben Zhao
 * File Created: Saturday, 25-02-08 09:54 am
 * Last Modified: Monday, 25-04-28 11:21 am, by Dw
 */

import { useCallback } from 'react';

import { create } from 'zustand';

import {
  createStoreActions,
  generateUseShallow,
  multiply,
  toFixedFloor,
} from '@web/utils';

import type { Currency } from '@/@types/asset';
import { getQuoteAsset } from '@/services/asset.service';

// eslint-disable-next-line import/no-cycle -- ignore
import { getUserAsset, useUserAsset } from './useUserAsset';

interface IState {
  /** 对应currency与 USD 的汇率值 */
  currencyRatioMap: Partial<Record<Currency, number>>;
  /** 正在请求汇率 */
  isRatioRequesting: boolean;
}

const defaultState: IState = {
  isRatioRequesting: false,
  currencyRatioMap: {
    USD: 1,
  },
};

interface IAction {
  setState: (_s: Partial<IState>) => void;
  /** 获取对应币种的汇率值 */
  fetchCurrencyRatio: (_currency: Currency) => Promise<number>;
  /** 根据usd值，拿到当前币种单位需显示的值，非响应式  */
  getCurrentAmount: (
    _usdVal?: number | string,
    _currency?: Currency,
  ) => Promise<string>;
}

const actions = createStoreActions<IState, IAction>((set, get) => ({
  setState(s: Partial<IState>) {
    const { currencyRatioMap, ...resS } = s;

    set({
      ...resS,
      currencyRatioMap: {
        ...get().currencyRatioMap,
        ...currencyRatioMap,
      },
    });
  },
  /** 获取对应币种的汇率值 */
  async fetchCurrencyRatio(currency: Currency) {
    useCurrencyRatioStore.setState({
      isRatioRequesting: true,
    });

    try {
      const { data } = await getQuoteAsset({
        base: 'USD',
        quote: currency,
      });

      useCurrencyRatioStore.setState({
        isRatioRequesting: false,
        currencyRatioMap: {
          ...get().currencyRatioMap,
          [currency]: data,
        },
      });

      return data;
    } catch (err) {
      useCurrencyRatioStore.setState({
        isRatioRequesting: false,
      });

      return Promise.reject(
        new Error(err instanceof Error ? err.message : String(err)),
      );
    }
  },
  /** 根据usd值，拿到当前币种单位需显示的值，非响应式  */
  async getCurrentAmount(usdVal?: number | string, currency?: Currency) {
    const amount = Number(usdVal) || 0;
    const { selectedCurrency } = getUserAsset();
    const { currencyRatioMap } = get();
    const curCurrency = currency || selectedCurrency;
    let ratio = currencyRatioMap[curCurrency];

    if (!ratio) {
      ratio = await getCurrencyRatioState().fetchCurrencyRatio(curCurrency);
    }

    const balance = multiply(amount, ratio || 1);

    return toFixedFloor(balance, 2);
  },
}));

const useCurrencyRatioStore = create<IAction & IState>((set, get) => ({
  ...defaultState,
  ...actions(set, get),
}));

export const useCurrencyRatio = generateUseShallow<IState, IAction>(
  useCurrencyRatioStore,
);

/** 函数式获取 store 的状态 */
export const getCurrencyRatioState = useCurrencyRatioStore.getState;

/** 获取响应式资产值 */
export const useDynamicBalanceFunc = () => {
  const { selectedCurrency } = useUserAsset(['selectedCurrency']);
  const { currencyRatioMap } = useCurrencyRatio(['currencyRatioMap']);

  const getAmount = useCallback(
    (usdVal?: number | string, currency?: Currency) => {
      const amount = Number(usdVal) || 0;

      const balance = multiply(
        amount,
        currencyRatioMap[currency || selectedCurrency] || 1,
      );

      return toFixedFloor(balance, 2) || '';
    },
    [currencyRatioMap, selectedCurrency],
  );

  return getAmount;
};
